import React, { useRef } from 'react';
import { HotFilterGroupConfiguration } from '@config/catalog/hotfilter-group-configurations';
import FilterList from '@components/catalog/filter-group/components/filter-list/filter-list';
import useFilterListFacets from '@hooks/filters/use-filter-list-facets';
import Switch from '@components/catalog/switch/switch';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { getLocalCountriesLabel } from '@hooks/filters/use-chip-list';
import useMarketingFiltersQuery from '@hooks/marketing-filters/use-marketing-filters-query';
import Icon from '@components/atoms/icon/icon';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { facetToFilterRename } from '@maps/catalog/search-product-facets-filters-maps';
import { isStringListFilterKey } from '@constants/catalog';
import styles from './catalog-hotfilter-checkbox-list.module.scss';

interface CatalogHotfilterCheckboxListProps {
  filterGroup: HotFilterGroupConfiguration;
}

const L2lCountryFilter = () => {
  const { filteredAndSortedFacets: localCountryFacets } = useFilterListFacets({ filterGroup: 'localCountries' });
  const onChangeHandler = useContextSelector(CatalogContext, (v) => v.toggleFilter);
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const { localToLocalFilter, freeShippingFilter } = useMarketingFiltersQuery();
  const { t } = useTranslation();

  const title = getLocalCountriesLabel({ localToLocalFilter, freeShippingFilter });

  if (!localCountryFacets?.length) {
    return null;
  }

  return (
    <div className={styles.checkboxList__l2lFilter}>
      <div>
        <div className={styles.checkboxList__l2lFilter__title}>
          <Icon
            className={styles.checkboxList__l2lFilter__icon}
            name="leaf-filled-default"
            ariaHidden
          />
          <span id={'l2l-filter-title'}>{title}</span>
        </div>
        <div className={clsx(styles.checkboxList__l2lFilter__subtitle, 'vc-text-s')}>
          {t('REGULAR_FILTER_LOCAL.SUSTAINABLE_LEAF.TEXT')}
        </div>
      </div>
      <div className={styles.checkboxList__l2lFilter__switch}>
        <Switch
          checked={!!filters.localCountries}
          onChange={() => onChangeHandler('localCountries', [])}
          ariaLabelledby={'l2l-filter-title'}
        />
      </div>
    </div>
  );
};

const CatalogHotfilterCheckboxList: React.FC<CatalogHotfilterCheckboxListProps> = ({
  filterGroup,
}: CatalogHotfilterCheckboxListProps) => {
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const filterKey = facetToFilterRename(filterGroup.facet);
  const filterGroupFilters = isStringListFilterKey(filterKey) ? filters[filterKey] : [];
  const selectedStringListFiltersRef = useRef<string[]>(filterGroupFilters ?? []);
  const { searchTerm, setSearchTerm, filteredAndSortedFacets } = useFilterListFacets({
    filterGroupSortBy: filterGroup?.sortBy,
    filterGroup: filterGroup.facet,
    selectedStringListFilters: selectedStringListFiltersRef.current,
  });

  const isCountryFilter = filterGroup.facet === 'country';

  return (
    <>
      {isCountryFilter && <L2lCountryFilter />}
      <div className={clsx(styles.checkboxList, isCountryFilter && styles.checkboxList__countryFilter)}>
        <FilterList
          filterGroup={filterGroup.facet}
          variant={'item'}
          facets={filteredAndSortedFacets}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searchable={!!filterGroup.isSearchable}
          filterConfiguration={{
            type: 'item',
            label: filterGroup.label,
            itemText: filterGroup.itemText,
          }}
          scrollLimit={isCountryFilter ? 5 : 8}
        />
      </div>
    </>
  );
};

export default CatalogHotfilterCheckboxList;
